import find from 'lodash/find';
import { computed, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import persistanceHelper from '@exchange/helpers/persistance-helper';
import { ordersService } from '@exchange/libs/order/my-orders/service/src';
import type { ReactiveOrdersData } from '@exchange/libs/order/my-orders/service/src';
import {
  type DivTableColumn,
  createColumnInstance,
  divTableSortDirections,
  createDivTableColumnInstance,
  type DivTableSortDirection,
} from '@exchange/libs/order/my-orders/ui/src/lib/components/div-table-helpers';
import { MyOrdersListType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';

interface TableSorting {
  sorted: string;
  sortDirection: DivTableSortDirection;
}

const getTimeFieldForSorting = (type: MyOrdersListType) => (type === MyOrdersListType.open ? 'time' : 'timeLastUpdated');

const getInitialTableSorting = (type: MyOrdersListType, tableSortingKey?: string) => {
  const defaultSorting = {
    sorted: getTimeFieldForSorting(type),
    sortDirection: divTableSortDirections.desc,
  };

  if (!tableSortingKey) {
    return defaultSorting;
  }

  const v = persistanceHelper.getObjFromJSON(tableSortingKey, undefined) as TableSorting | undefined;

  if (!v) {
    return defaultSorting;
  }

  return v;
};

export const useOrdersListMobile = () => {
  const { t } = useI18n({ useScope: 'global' });

  const mobileHeader = computed(() => [
    createColumnInstance({
      title: t('modules.myOrders.header.symbol'),
      columnClass: 'symbol',
    }),
    createColumnInstance({
      title: `${t('modules.myOrders.header.price')} / ${t('modules.myOrders.header.amount')}`,
      columnClass: 'price',
    }),
  ]);

  return {
    mobileHeader,
    cancelOrder: ordersService.cancelOrder.bind(ordersService),
  };
};

export const usePositionsListDesktop = (tableSortingKey: string, currentBreakPoint: Ref<string>) => {
  const { t } = useI18n({ useScope: 'global' });

  const tableSorting = ref<TableSorting>(getInitialTableSorting(MyOrdersListType.positions, tableSortingKey));

  const computedTableSorting = computed({
    get: () => tableSorting.value,
    set: (value) => {
      persistanceHelper.localstorageSet(tableSortingKey, value);
      tableSorting.value = value;
    },
  });

  const getSorted = (name: string) => tableSorting.value?.sorted.includes(name);

  const getSortDirection = (name: string) => (tableSorting.value?.sorted.includes(name) ? tableSorting.value.sortDirection : undefined);

  const saveSorting = (event: Array<DivTableColumn<ReactiveOrdersData>>) => {
    const obj = find(event, ['sorted', true]);

    if (!obj) {
      return;
    }

    computedTableSorting.value = {
      sorted: obj.columnClass,
      sortDirection: obj.sortDirection,
    };
  };

  const desktopHeader = computed(() => [
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.symbol'),
      columnClass: 'market align-left',
      sortFunction: () => 0,
      sorted: getSorted('market'),
      sortDirection: getSortDirection('market'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.side'),
      columnClass: 'side align-left',
      sortFunction: () => 0,
      sorted: getSorted('side'),
      sortDirection: getSortDirection('side'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.size'),
      columnClass: 'size align-right',
      sortFunction: () => 0,
      sorted: getSorted('size'),
      sortDirection: getSortDirection('size'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.positionValue'),
      columnClass: 'position-value align-right',
      sortFunction: () => 0,
      sorted: getSorted('position-value'),
      sortDirection: getSortDirection('position-value'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.averageEntryPrice'),
      columnClass: 'entry-price align-right',
      sortFunction: () => 0,
      sorted: getSorted('entry-price'),
      sortDirection: getSortDirection('entry-price'),
      hidden: false,
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.markPrice'),
      columnClass: 'mark-price align-right',
      sortFunction: () => 0,
      sorted: getSorted('mark-price'),
      sortDirection: getSortDirection('mark-price'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.lastSettlement'),
      columnClass: 'last-settlement align-right',
      sortFunction: () => 0,
      sorted: getSorted('last-settlement'),
      sortDirection: getSortDirection('last-settlement'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.realisedPnl'),
      columnClass: 'realised-pnl align-right',
      sortFunction: () => 0,
      sorted: getSorted('realised-pnl'),
      sortDirection: getSortDirection('realised-pnl'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.unrealisedPnl'),
      columnClass: 'unrealised-pnl align-right',
      sortFunction: () => 0,
      sorted: getSorted('unrealised-pnl'),
      sortDirection: getSortDirection('unrealised-pnl'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    // TODO: uncomment when we have way to show state for futures
    // createDivTableColumnInstance({
    //   title: 'State',
    //   columnClass: 'state align-right',
    //   sortFunction: () => 0,
    //   sorted: getSorted('state'),
    //   sortDirection: getSortDirection('state'),
    //   hidden: currentBreakPoint.value.includes('hide-time'),
    // }),
    createDivTableColumnInstance({
      title: t('modules.myOrders.header.marginUsed'),
      columnClass: 'margin-used align-right',
      sortFunction: () => 0,
      sorted: getSorted('margin-used'),
      sortDirection: getSortDirection('margin-used'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    createDivTableColumnInstance({
      title: '',
      columnClass: 'close-position align-right',
      sortFunction: () => 0,
      sorted: getSorted('close-position'),
      sortDirection: getSortDirection('close-position'),
      hidden: currentBreakPoint.value.includes('hide-time'),
    }),
    // Disable flip for now
    // createDivTableColumnInstance({
    //   title: 'Flip',
    //   columnClass: 'flip align-right',
    //   sortFunction: () => 0,
    //   sorted: getSorted('flip'),
    //   sortDirection: getSortDirection('flip'),
    //   hidden: currentBreakPoint.value.includes('hide-time'),
    // }),
  ]);

  return {
    desktopHeader,
    saveSorting,
    cancelOrder: ordersService.cancelOrder.bind(ordersService),
  };
};
